import { useNavigate, useParams } from 'react-router';
import { useState } from 'react';
import { createExcelModel, addCustomModel } from '../../api/registry';
import { registryActions } from '@/stores/registryStore';
import ImportModelForm from './ImportModelForm';
import TeaInputs from './TeaInputs';
import { FormProvider, useForm } from 'react-hook-form';
import SubmitButton from '../core/form/SubmitButton';
import { useSpeciesData } from './hooks/useSpeciesData';
import { parseErrorMessage } from '@/utils/errorUtils';

const ImportModelModal = () => {
  const [status, setStatus] = useState({
    isLoading: false,
    isSubmitting: false,
    isUploading: false,
  });
  const [errorMessage, setErrorMessage] = useState(null);
  const [formPart, setFormPart] = useState('upload');
  const [hasTeaData, setHasTeaData] = useState(false);
  const params = useParams();
  const navigate = useNavigate();
  const { typeToUnitsMap } = useSpeciesData();

  const methods = useForm({
    defaultValues: {
      name: '',
      description: '',
      file: null,
      tea_type: 'simple',
      excel_id: '',
      outputs: [],
      label: '',
      permissions: 'creator',
      project_ids: [],
      simpleCosts: {
        spec_type: 'process_only',
        levelized_costs: [],
      },
      bottomUpCosts: {
        reference_product: '',
        reference_capacity: 0,
        conversions: {
          reference_capacity: '',
        },
      },
    },
  });

  const handleClose = () => {
    const { pathwayId, projectId, caseId } = params;

    navigate(`/projects/${projectId}/build/pathways/${pathwayId}/${caseId}`, { replace: true });
  };

  const handleFileUpload = async file => {
    if (!file) return;

    try {
      setStatus(prev => ({ ...prev, isUploading: true }));
      setErrorMessage(null);

      const formDataToSend = new FormData();
      formDataToSend.append('file', file);

      const response = await createExcelModel(formDataToSend);
      const { data: { outputs, excel_id, tea } = {} } = response ?? {};

      methods.setValue('outputs', outputs);
      methods.setValue('excel_id', excel_id);
      setHasTeaData(tea || false);
    } catch (error) {
      const message = parseErrorMessage(error);
      setErrorMessage(message);
    } finally {
      setStatus(prev => ({ ...prev, isUploading: false }));
    }
  };

  const handleContinueToTea = () => {
    setFormPart('tea');
  };

  const handleTeaSubmit = async data => {
    try {
      setStatus(prev => ({ ...prev, isSubmitting: true }));
      setErrorMessage(null);

      const processedData = {
        model_type: 'excel',
        permissions: data.permissions,
        data: {
          label: data.label,
          // The API rejects a blank description but accepts none.
          description: data.description?.trim() || undefined,
          excel_id: data.excel_id,
        },
      };

      if (data.permissions === 'project') {
        processedData.project_ids = data.project_ids;
      }

      if (hasTeaData) {
        processedData.data.tea = {
          tea_type: data.tea_type,
        };

        if (data.tea_type === 'simple') {
          processedData.data.tea.data = {
            spec_type: data.simpleCosts.spec_type,
            levelized_costs: data.simpleCosts.levelized_costs.map(item => ({
              cost: { scalar: item.cost.scalar, unit: JSON.parse(item.cost.unit) },
              product: item.product,
            })),
          };
        } else {
          processedData.data.tea.data = {
            ...data.bottomUpCosts,
            reference_capacity: {
              scalar: data.bottomUpCosts.reference_capacity,
              unit: JSON.parse(data.bottomUpCosts.conversions.reference_capacity),
            },
          };
        }
      } else {
        processedData.data.tea = null;
      }

      const { data: created } = await addCustomModel(processedData);
      await registryActions.refresh();
      // Land the user on the new module in the palette, as the equation
      // builder does, rather than on a success message.
      registryActions.highlightCustomModule(created?.id);
      handleClose();
    } catch (error) {
      const message = parseErrorMessage(error);
      setErrorMessage(message);
    } finally {
      setStatus(prev => ({ ...prev, isSubmitting: false }));
    }
  };

  const handlePreviousPart = () => setFormPart('upload');

  const { isSubmitting, isUploading } = status;
  const { watch } = methods;
  const name = watch('label');
  const file = watch('file');
  const isValid = name && file;

  return (
    <dialog className="modal" open>
      <div className="modal-box w-4/5 max-w-5xl p-8">
        <button className="btn btn-sm btn-circle btn-ghost absolute right-2 top-2" onClick={handleClose}>
          ✕
        </button>

        <h3 className="font-bold text-lg text-center mb-4">Upload a Model</h3>

        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(handleTeaSubmit)}>
            {formPart === 'upload' ? (
              <>
                <ImportModelForm
                  isUploading={isUploading}
                  errorMessage={errorMessage}
                  onFileChange={handleFileUpload}
                />
                <div className="flex justify-center mt-6">
                  <button
                    type="button"
                    className={`btn btn-accent btn-sm ${!isValid || isUploading ? 'btn-disabled' : ''}`}
                    onClick={hasTeaData ? handleContinueToTea : methods.handleSubmit(handleTeaSubmit)}
                    disabled={!isValid || isUploading}
                  >
                    {isUploading ? (
                      <>
                        <span className="loading loading-spinner"></span>
                        Please wait...
                      </>
                    ) : hasTeaData ? (
                      'Continue to Part 2: TEA'
                    ) : (
                      'Save'
                    )}
                  </button>
                </div>
              </>
            ) : hasTeaData ? (
              <>
                <TeaInputs typeToUnitsMap={typeToUnitsMap} />
                <div className="flex justify-center mt-6 gap-4">
                  <button type="button" onClick={handlePreviousPart} className="btn btn-outline btn-sm">
                    Back to Part 1
                  </button>
                  <SubmitButton label="Save" isPending={isSubmitting} />
                </div>
              </>
            ) : null}
          </form>
        </FormProvider>
      </div>
    </dialog>
  );
};

export default ImportModelModal;
